import type { EmailDropdownProps, PendingEmail } from '../types/components'
import { useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { sendApplicationEmail } from '../services/applicationService'
import { EmailBroadcastModal } from './modals/EmailBroadcastModal'
import { EmailMissingInfoModal } from './modals/EmailMissingInfoModal'
import { EmailCreditErrorModal } from './modals/EmailCreditErrorModal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { showSuccess, showError } from '@/lib/utils/notifications'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { isNonActiveRentalStatus } from '../pages/applicationDetailHelpers'
import styles from '../styles/actionButtons.module.css'


/** Labels for `Send ${label} Mail to Resident` — Angular `mailType` in application-details.component.ts */
const MAIL_TYPE_LABEL: Record<string, string> = {
  GENERATE_CERTIFICATE: 'Generate Contract',
  APPLICATION_CREATION: 'Application Creation',
  APPROVED_APPLICATION: 'Approved Application',
  DENIED_APPLICATION: 'Denied Application',
  INCOMPLETE_APPLICATION: 'Incomplete Application',
  CANCELLED_APPLICATION: 'Cancellation Email',
  LIBERTY_RESIDENT_INFORMATION: 'Liberty Rent Information',
}

/** Pre-send confirm title/message — Angular sendMail(type) popupmessage parity. */
function getSendMailConfirm(type: string): { title: string; message: string } {
  let message: string
  if (type === 'GENERATE_CERTIFICATE') {
    message = 'Send Contract to Property'
  } else if (type === 'CANCELLED_APPLICATION') {
    message = 'Send Cancellation Email to Property'
  } else {
    const label = MAIL_TYPE_LABEL[type] ?? type
    message = `Send ${label} Mail to Resident`
  }
  return { title: 'Send Email', message }
}

/** Email dropdown menu — each item maps to API type (Angular sendMail). */

export function EmailDropdown({
  applicantId,
  applicationAllData,
  applicationData,
  canEmailCreditError,
  canEmailLibertyInfo,
  disabled,
  onEmailSent,
  onResendDeniedEmail,
}: EmailDropdownProps) {
  const [showMenu, setShowMenu] = useState(false)
  const [showBroadcastModal, setShowBroadcastModal] = useState(false)
  const [showMissingInfoModal, setShowMissingInfoModal] = useState(false)
  const [showCreditErrorModal, setShowCreditErrorModal] = useState(false)
  const [pendingEmail, setPendingEmail] = useState<PendingEmail | null>(null)
  const [sending, setSending] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  /** Configure the pending send and show the confirmation dialog. The actual
   *  network call happens in performSend() once the user confirms. */
  const requestSend = (type: string) => {
    // Angular sendMail() blocks GENERATE_CERTIFICATE when rental status is non-active.
    if (type === 'GENERATE_CERTIFICATE' && isNonActiveRentalStatus(applicationAllData?.rental_status)) {
      return
    }
    setShowMenu(false)
    setPendingEmail({ kind: 'app', type })
  }

  const performSend = async () => {
    if (!pendingEmail || sending) return
    setSending(true)
    try {
      await sendApplicationEmail(applicantId, pendingEmail.type)
      trackApplicationEvent('application_email_sent', 'email', {
        email_type: pendingEmail.type.toLowerCase(),
      })
      showSuccess('Email Send Successfully!')
      onEmailSent()
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message
      showError(typeof msg === 'string' && msg ? msg : 'Failed to send email')
    } finally {
      setSending(false)
      setPendingEmail(null)
    }
  }

  const accessPaidConfirmationDisabledForRentalStatus = isNonActiveRentalStatus(
    applicationAllData?.rental_status,
  )
  const canSendCertificate =
    !!applicationAllData?.certificate_path && !accessPaidConfirmationDisabledForRentalStatus
  const canSendApproval = applicationData?.applicant_status === 'APPROVED'
  const canSendDenial = applicationData?.applicant_status === 'DENIED'
  const canSendIncomplete = applicationData?.applicant_status === 'INCOMPLETE'

  return (
    <div
      className={styles.emailDropdown}
      ref={dropdownRef}
      data-testid="application-detail-email-dropdown"
    >
      <button
        type="button"
        className={`${styles.btn} ${styles.emailDropdownBtn}`}
        data-testid="application-detail-email-toggle-button"
        onClick={() => setShowMenu(!showMenu)}
        disabled={disabled}
        aria-expanded={showMenu}
        aria-haspopup="menu"
      >
        Email
        <ChevronDown className={styles.chevron} aria-hidden />
      </button>

      {showMenu && (
        <ul className={styles.emailMenu} data-testid="application-detail-email-menu" role="menu">
          <li role="none">
            <button
              type="button"
              role="menuitem"
              data-testid="application-detail-email-resend-application-email-button"
              onClick={() => requestSend('APPLICATION_CREATION')}
            >
              Resend Application Email
            </button>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              data-testid="application-detail-email-access-paid-confirmation-button"
              onClick={() => requestSend('GENERATE_CERTIFICATE')}
              disabled={!canSendCertificate}
            >
              Email Access Paid Confirmation
            </button>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              data-testid="application-detail-email-resend-approval-email-button"
              onClick={() => requestSend('APPROVED_APPLICATION')}
              disabled={!canSendApproval}
            >
              Resend Approval Email
            </button>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              data-testid="application-detail-email-resend-denial-email-button"
              onClick={() => {
                setShowMenu(false)
                onResendDeniedEmail?.()
              }}
              disabled={!canSendDenial || !onResendDeniedEmail}
            >
              Resend Denial Email
            </button>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              data-testid="application-detail-email-email-reminder-to-applicant-button"
              onClick={() => requestSend('INCOMPLETE_APPLICATION')}
              disabled={!canSendIncomplete}
            >
              Email Reminder to Applicant
            </button>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              data-testid="application-detail-email-send-email-button"
              onClick={() => {
                setShowMenu(false)
                setShowBroadcastModal(true)
              }}
            >
              Send Email
            </button>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              data-testid="application-detail-email-send-missing-info-email-button"
              onClick={() => {
                setShowMenu(false)
                setShowMissingInfoModal(true)
              }}
            >
              Send Missing Info Email
            </button>
          </li>
          {canEmailCreditError && (
            <li role="none">
              <button
                type="button"
                role="menuitem"
                data-testid="application-detail-email-send-credit-report-error-email-button"
                onClick={() => {
                  setShowMenu(false)
                  setShowCreditErrorModal(true)
                }}
              >
                Send Credit Report Error Email
              </button>
            </li>
          )}
          <li role="none">
            <button
              type="button"
              role="menuitem"
              data-testid="application-detail-email-resend-cancellation-email-button"
              onClick={() => requestSend('CANCELLED_APPLICATION')}
            >
              Resend Cancellation Email
            </button>
          </li>
          {canEmailLibertyInfo && (
            <li role="none">
              <button
                type="button"
                role="menuitem"
                data-testid="application-detail-email-send-liberty-rent-info-email-button"
                onClick={() => requestSend('LIBERTY_RESIDENT_INFORMATION')}
              >
                Send Liberty Rent Info Email
              </button>
            </li>
          )}
        </ul>
      )}

      <ConfirmDialog
        isOpen={pendingEmail !== null}
        title={pendingEmail ? getSendMailConfirm(pendingEmail.type).title : ''}
        message={pendingEmail ? getSendMailConfirm(pendingEmail.type).message : ''}
        primaryLabel={sending ? 'Sending...' : 'Yes'}
        secondaryLabel="No"
        onPrimary={() => void performSend()}
        onSecondary={() => setPendingEmail(null)}
        presentation="saveChanges"
        overlayDataTestId="application-detail-email-dropdown-confirm-send-mail"
        primaryButtonDataTestId="application-detail-email-dropdown-confirm-send-mail-primary-button"
        secondaryButtonDataTestId="application-detail-email-dropdown-confirm-send-mail-secondary-button"
        closeButtonDataTestId="application-detail-email-dropdown-confirm-send-mail-close-button"
      />

      {showBroadcastModal && (
        <EmailBroadcastModal
          applicantId={applicantId}
          applicationAllData={applicationAllData}
          applicationData={applicationData}
          onClose={() => setShowBroadcastModal(false)}
          onSuccess={onEmailSent}
        />
      )}

      {showMissingInfoModal && (
        <EmailMissingInfoModal
          applicantId={applicantId}
          applicationAllData={applicationAllData}
          applicationData={applicationData}
          onClose={() => setShowMissingInfoModal(false)}
          onSuccess={onEmailSent}
        />
      )}

      <EmailCreditErrorModal
        isOpen={showCreditErrorModal}
        applicantId={applicantId}
        onClose={() => setShowCreditErrorModal(false)}
        onSuccess={onEmailSent}
      />
    </div>
  )
}
